import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import MobileFrame from "../../components/MobileFrame";

import { sendOtp } from "../../api/auth.api";
import { ApiError } from "../../api/ApiError";

import type {
    RootState,
    AppDispatch,
} from "../../store";

import { setOtpSession } from "../../store/authSlice";

const RESEND_TIMER_KEY = "otp_resend_available_at";
const RESEND_WAIT_SECONDS = 180;


const LoginPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [consentCreditCheck, setConsentCreditCheck] =
        useState(false);

    const [consentDataUse, setConsentDataUse] =
        useState(false);


    // --------------------------------------------------
    // Journey
    // --------------------------------------------------

    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );

    const mobile =
        journey?.customerMobileMasked ?? "";


    // --------------------------------------------------
    // Form validation
    // --------------------------------------------------

    const isFormValid =
        Boolean(mobile) &&
        consentCreditCheck &&
        consentDataUse;


    // --------------------------------------------------
    // Login / Send OTP
    // --------------------------------------------------

    const handleLogin = async () => {
        // Explicit double-submit protection.
        if (loading) return;

        // Journey is required to initiate OTP.
        if (!journey) {
            setError(
                "Your application session is unavailable. Please open the application link again."
            );
            return;
        }

        if (!isFormValid) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Request OTP from backend.
            const response = await sendOtp(
                journey.journeyId
            );

            // Store OTP session in Redux.
            //
            // IMPORTANT:
            // The backend should NOT return devOtp in production.
            // The frontend check below is only an additional safeguard.
            dispatch(
                setOtpSession({
                    otpToken:
                        response.data.otpToken,

                    expiresInSeconds:
                        response.data.expiresInSeconds,

                    devOtp:
                        import.meta.env.DEV
                            ? response.data.devOtp
                            : undefined,
                })
            );

            /*
             * Start the resend cooldown now — this is
             * when the OTP is actually sent. OtpPage reads
             * this same key, so the countdown is correct
             * the instant it mounts, instead of showing
             * "Resend OTP" with no timer.
             */
            sessionStorage.setItem(
                RESEND_TIMER_KEY,
                String(
                    Date.now() +
                    RESEND_WAIT_SECONDS * 1000
                )
            );

            // Move to OTP screen only after
            // successful OTP session creation.
            navigate(
                "/nverify-otp",
                {
                    replace: true,
                }
            );

        } catch (error) {
            console.error(
                "Failed to send OTP:",
                error
            );

            let message =
                "Unable to send OTP. Please try again.";

            if (error instanceof ApiError) {
                message =
                    error.message?.trim() ||
                    message;

            } else if (error instanceof Error) {
                message =
                    error.message?.trim() ||
                    message;
            }

            setError(message);

        } finally {
            setLoading(false);
        }
    };


    // --------------------------------------------------
    // Consent handlers
    // --------------------------------------------------

    const handleCreditConsentChange = (
        checked: boolean
    ) => {
        setConsentCreditCheck(checked);

        if (error) {
            setError(null);
        }
    };


    const handleDataConsentChange = (
        checked: boolean
    ) => {
        setConsentDataUse(checked);

        if (error) {
            setError(null);
        }
    };


    // --------------------------------------------------
    // UI
    // --------------------------------------------------

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background decoration */}
                <div
                    aria-hidden="true"
                    className="absolute w-64 h-64 bg-purple-200 rounded-full top-[-90px] left-[-90px] blur-3xl opacity-20"
                />

                <div
                    aria-hidden="true"
                    className="absolute w-64 h-64 bg-blue-200 rounded-full bottom-[-120px] right-[-80px] blur-3xl opacity-20"
                />


                <div className="relative z-10 flex flex-col h-full px-6 pt-15">

                    {/* ==================================================
                        Header / Illustration
                    ================================================== */}

                    <div className="text-center mb-6">

                        {/* Decorative illustration */}
                        <div
                            aria-hidden="true"
                            className="relative flex justify-center mb-13 -mt-7"
                        >
                            <div className="relative w-60 h-48">

                                <div className="absolute inset-[-90px]">

                                    <div className="absolute w-72 h-40 bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-100 opacity-60 blur-3xl rounded-full top-6 left-[-40px]" />

                                    <div className="absolute w-full h-full opacity-40">

                                        <svg
                                            viewBox="0 0 200 120"
                                            className="w-full h-full"
                                            aria-hidden="true"
                                            focusable="false"
                                        >
                                            <path
                                                d="M0,60 C40,20 80,100 120,60 C150,30 170,80 200,50"
                                                stroke="#6366f1"
                                                strokeWidth="2"
                                                fill="transparent"
                                                className="opacity-40"
                                            />

                                            <path
                                                d="M0,80 C50,40 90,110 140,70 C170,40 190,90 200,70"
                                                stroke="#3b82f6"
                                                strokeWidth="2"
                                                fill="transparent"
                                                className="opacity-30"
                                            />
                                        </svg>

                                    </div>
                                </div>


                                {/* Decorative card */}
                                <div className="absolute w-24 h-14 bg-green-100 border border-green-200 rounded-lg shadow-sm rotate-[10deg] bottom-16 right-4">

                                    <div className="absolute w-full h-2 bg-green-300 top-5 opacity-70" />

                                    <div className="absolute w-10 h-1 bg-green-400 rounded-full top-2 left-3 opacity-60" />

                                    <div className="absolute w-12 h-1 bg-green-400 rounded-full bottom-2 left-3 opacity-50" />

                                </div>


                                {/* Decorative rupee */}
                                <div className="absolute w-5 h-5 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-400 shadow-md flex items-center justify-center top-12 right-10 animate-bounce">

                                    <span className="text-xs font-bold text-yellow-900">
                                        ₹
                                    </span>

                                    <div className="absolute w-full h-full bg-yellow-100 rounded-full blur-sm opacity-30" />

                                </div>


                                {/* Decorative rupee */}
                                <div className="absolute w-5 h-5 rounded-full bg-gradient-to-br from-yellow-200 to-yellow-400 shadow-md flex items-center justify-center bottom-16 right-4">

                                    <span className="text-xs font-bold text-yellow-900">
                                        ₹
                                    </span>

                                    <div className="absolute w-full h-full bg-yellow-100 rounded-full blur-sm opacity-30" />

                                </div>

                            </div>
                        </div>


                        {/* Brand */}
                        <div className="flex flex-col items-center justify-center">

                            <img
                                src="/CleverPeLogos/CleverPe_Black_H.svg"
                                alt="CleverPe"
                                className="h-8 object-contain"
                            />

                            <p className="mt-2 text-sm text-center bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 text-transparent bg-clip-text font-semibold">
                                India's First Intelligent Commerce Platform
                            </p>

                        </div>

                    </div>


                    {/* ==================================================
                        Login Card
                    ================================================== */}

                    <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-2xl">

                        <h2 className="mb-4 text-medium font-semibold text-gray-800">
                            Login to continue
                        </h2>


                        {/* Mobile number */}
                        <div className="mb-4">

                            <div className="flex mt-1 overflow-hidden bg-gray-50 border border-gray-200 rounded-xl">

                                <span
                                    aria-hidden="true"
                                    className="px-3 py-3 text-medium text-gray-500 bg-gray-100 border-r border-gray-100"
                                >
                                    +91
                                </span>

                                <input
                                    value={mobile}
                                    readOnly
                                    disabled
                                    type="tel"
                                    aria-label="Registered mobile number"
                                    className="w-full px-3 py-3 text-medium bg-gray-50 !text-black outline-none cursor-not-allowed disabled:opacity-100"
                                />

                            </div>

                        </div>


                        {/* ==================================================
                            Consents
                        ================================================== */}

                        <div className="mt-4 mb-4 space-y-3">

                            {/* Credit check consent */}
                            <label className="flex items-start gap-2 text-xs text-gray-500 leading-snug cursor-pointer">

                                <input
                                    type="checkbox"
                                    checked={
                                        consentCreditCheck
                                    }
                                    onChange={(e) =>
                                        handleCreditConsentChange(
                                            e.target.checked
                                        )
                                    }
                                    aria-describedby="credit-check-consent"
                                    className="mt-0.5 accent-indigo-500"
                                />

                                <span id="credit-check-consent">
                                    I agree to a pre-approval credit check and sharing my data with credit bureaus or authorized institutions as per RBI guidelines.
                                </span>

                            </label>


                            {/* Data usage consent */}
                            <label className="flex items-start gap-2 text-xs text-gray-500 leading-snug cursor-pointer">

                                <input
                                    type="checkbox"
                                    checked={
                                        consentDataUse
                                    }
                                    onChange={(e) =>
                                        handleDataConsentChange(
                                            e.target.checked
                                        )
                                    }
                                    aria-describedby="data-use-consent"
                                    className="mt-0.5 accent-indigo-500"
                                />

                                <span id="data-use-consent">
                                    I understand my data will be used only to check eligibility and creditworthiness for financial products/services.
                                </span>

                            </label>

                        </div>


                        {/* ==================================================
                            Error
                        ================================================== */}

                        {error && (
                            <p
                                role="alert"
                                aria-live="polite"
                                className="mb-3 text-center text-xs text-red-500"
                            >
                                {error}
                            </p>
                        )}


                        {/* ==================================================
                            Continue
                        ================================================== */}

                        <button
                            type="button"
                            onClick={handleLogin}
                            disabled={
                                !isFormValid ||
                                loading
                            }
                            aria-busy={loading}
                            className="
                                w-full
                                py-3
                                text-m
                                font-semibold
                                text-white
                                transition
                                rounded-full
                                bg-gradient-to-r
                                from-indigo-500
                                to-blue-400
                                hover:opacity-90
                                active:scale-[0.99]
                                disabled:opacity-50
                                disabled:cursor-not-allowed
                            "
                        >
                            {loading
                                ? "Sending OTP..."
                                : "Continue"}
                        </button>

                    </div>


                    {/* ==================================================
                        Footer
                    ================================================== */}

                    <div className="flex-1" />

                    <p className="pb-4 mt-4 text-[10px] text-center text-gray-400">

                        By continuing, you agree to our{" "}

                        <br />

                        <span className="text-indigo-500 font-medium">
                            Terms &amp; Conditions
                        </span>

                        {" "}and{" "}

                        <span className="text-indigo-500 font-medium">
                            Privacy Policy
                        </span>

                    </p>

                </div>

            </div>
        </MobileFrame>
    );
};

export default LoginPage;